import { supabase } from '../lib/supabase';

/**
 * Check if error is a schema-related error that should be thrown
 */
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

/**
 * Get all announcements ordered by creation date
 */
export const getAnnouncements = async () => {
  try {
    const { data, error } = await supabase?.from('announcements')?.select('*')?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error in getAnnouncements:', error?.message);
        throw error;
      }
      console.error('Error fetching announcements:', error?.message);
      return [];
    }

    return data?.map(announcement => ({
      id: announcement?.id,
      title: announcement?.title,
      message: announcement?.message,
      priority: announcement?.priority,
      timestamp: announcement?.created_at,
      read: false // Client-side property for UI state
    })) || [];
  } catch (error) {
    if (isSchemaError(error)) {
      throw error;
    }
    console.error('Error in getAnnouncements:', error);
    return [];
  }
};

/**
 * Create a new announcement (admin only)
 */
export const createAnnouncement = async (announcementData) => {
  try {
    // Get current user
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      throw new Error('Authentication required');
    }

    const { data, error } = await supabase?.from('announcements')?.insert({
        title: announcementData?.title,
        message: announcementData?.message,
        priority: announcementData?.priority || 'low',
        created_by: user?.id
      })?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error in createAnnouncement:', error?.message);
        throw error;
      }
      throw new Error(error?.message || 'Failed to create announcement');
    }

    return data;
  } catch (error) {
    console.error('Error creating announcement:', error);
    throw error;
  }
};

/**
 * Update an existing announcement
 */
export const updateAnnouncement = async (announcementId, updates) => {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      throw new Error('Authentication required');
    }

    const { data, error } = await supabase?.from('announcements')?.update({
        title: updates?.title,
        message: updates?.message,
        priority: updates?.priority
      })?.eq('id', announcementId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error in updateAnnouncement:', error?.message);
        throw error;
      }
      throw new Error(error?.message || 'Failed to update announcement');
    }

    return data;
  } catch (error) {
    console.error('Error updating announcement:', error);
    throw error;
  }
};

/**
 * Delete an announcement
 */
export const deleteAnnouncement = async (announcementId) => {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    
    if (authError || !user) {
      throw new Error('Authentication required');
    }

    const { error } = await supabase?.from('announcements')?.delete()?.eq('id', announcementId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error in deleteAnnouncement:', error?.message);
        throw error;
      }
      throw new Error(error?.message || 'Failed to delete announcement');
    }

    return true;
  } catch (error) {
    console.error('Error deleting announcement:', error);
    throw error;
  }
};

export const announcementService = {
  getAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement
};